import type { TokenPair } from '@compass/client/types/auth';
import type { User } from '@compass/client/types/user';
import { NonAuthSidebar } from '@components/layouts';
import { getErrorMessage } from '@utils/errorMessages';
import { AlertCircle, Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { MfaVerifyForm } from '@/components/mfa/MfaVerifyForm';
import { Button } from '@/components/ui/button';
import { saveAuthCredentials } from '@/hooks/useAuth';
import { consumeSsoRedirect, useCompleteSsoLogin } from '@/hooks/useSso';
import { useStore } from '@/store';
import { goToPostLoginTarget } from '@/utils/postLoginNavigation';

interface MfaState {
  required: boolean;
  challengeToken: string;
  methods: string[];
}

export function SSOCallback() {
  const navigate = useNavigate();
  const { connectionId } = useParams<{ connectionId: string }>();
  const [searchParams] = useSearchParams();
  const { setUser, setIsAuthenticated } = useStore();
  const completeSsoLogin = useCompleteSsoLogin();
  const [mfaState, setMfaState] = useState<MfaState | null>(null);
  const hasAttempted = useRef(false);

  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const oauthError = searchParams.get('error');
  const oauthErrorDescription = searchParams.get('error_description');

  const handleLoginSuccess = useCallback(
    (user: User, tokenPair: TokenPair) => {
      if (user && saveAuthCredentials(user, tokenPair)) {
        setUser(user);
        setIsAuthenticated(true);
      }
      toast.success('SSO login successful');
      // Return the user to the page they were on before SSO (stashed by LoginForm
      // before the IdP round-trip); falls back to '/' when there was no destination.
      goToPostLoginTarget(consumeSsoRedirect(), navigate);
    },
    [navigate, setUser, setIsAuthenticated],
  );

  useEffect(() => {
    if (hasAttempted.current) return;
    if (oauthError) return; // IdP returned an error, don't attempt login
    if (!code || !state || !connectionId) return;

    hasAttempted.current = true;

    completeSsoLogin.mutate(
      { connection_id: connectionId, code, state },
      {
        onSuccess: data => {
          if (data.mfa_required && data.mfa_challenge_token) {
            setMfaState({
              required: true,
              challengeToken: data.mfa_challenge_token,
              methods: data.mfa_methods || [],
            });
            return;
          }

          if (data.user && data.token_pair) {
            handleLoginSuccess(data.user, data.token_pair);
          }
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, state, connectionId]);

  const missingParams = !code || !state || !connectionId;

  return (
    <div className="h-screen w-full flex">
      <NonAuthSidebar />
      <div className="flex-1 flex items-center justify-center p-8 overflow-hidden">
        {oauthError ? (
          <div className="w-full max-w-md space-y-6 text-center">
            <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                <div className="flex-1 text-left">
                  <p className="font-medium">SSO Login Failed</p>
                  <p className="mt-1">
                    {oauthErrorDescription ||
                      'The identity provider returned an error. Please try again.'}
                  </p>
                </div>
              </div>
            </div>
            <Button className="w-full h-12" onClick={() => navigate('/login', { replace: true })}>
              Back to Login
            </Button>
          </div>
        ) : mfaState?.required ? (
          <MfaVerifyForm
            challengeToken={mfaState.challengeToken}
            methods={mfaState.methods}
            onSuccess={(user, tokenPair) => handleLoginSuccess(user, tokenPair)}
            onBack={() => {
              setMfaState(null);
              navigate('/login', { replace: true });
            }}
          />
        ) : missingParams ? (
          <div className="w-full max-w-md space-y-6 text-center">
            <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                <div className="flex-1 text-left">
                  <p className="font-medium">Invalid SSO Callback</p>
                  <p className="mt-1">Missing required parameters. Please try signing in again.</p>
                </div>
              </div>
            </div>
            <Button className="w-full h-12" onClick={() => navigate('/login', { replace: true })}>
              Back to Login
            </Button>
          </div>
        ) : completeSsoLogin.isError ? (
          <div className="w-full max-w-md space-y-6 text-center">
            <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                <div className="flex-1 text-left">
                  <p className="font-medium">SSO Login Failed</p>
                  <p className="mt-1">
                    {getErrorMessage(completeSsoLogin.error) ||
                      'Failed to complete SSO login. Please try again.'}
                  </p>
                </div>
              </div>
            </div>
            <Button className="w-full h-12" onClick={() => navigate('/login', { replace: true })}>
              Back to Login
            </Button>
          </div>
        ) : (
          <div className="w-full max-w-md space-y-6 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold">Completing Sign In</h2>
              <p className="text-muted-foreground">Please wait while we verify your identity...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
